import { isErrorCode, type ErrorCode } from './protocol.ts'
import { validateTheme } from './theme.ts'
import type {
  AttemptState,
  CardDraft,
  CardSummary,
  PaymentDecline,
  PaymentMethodSummary,
  PaymentQuote,
  PaymentRecord,
  SessionConfig,
  SubmitOutcome
} from './types.ts'
import { MAX_PAYMENT_ATTEMPTS } from './types.ts'
import {
  integerOrNull,
  isRecord,
  isStringArray,
  paymentMethodPkid
} from './guards.ts'

export const TERMINAL_PATHS = {
  exchange: '/v1/terminal/sessions/exchange',
  paymentMethods: '/v1/terminal/payment-methods',
  paymentAttempts: '/v1/terminal/payment-attempts',
  payments: '/v1/terminal/payments'
} as const

// A processor call can take 40 seconds. A shorter browser timeout reports an unknown outcome early.
export const PAYMENT_TIMEOUT_MS = 60_000

const MAX_DECLINE_REASON = 200

const MESSAGES: Record<ErrorCode, string> = {
  unauthorized: 'This request is not authorized.',
  terminal_not_found: 'The terminal was not found.',
  session_not_found: 'The session was not found.',
  payment_not_found: 'The payment was not found.',
  payment_method_not_found: 'The payment method was not found.',
  account_not_found: 'The account was not found.',
  server_error: 'The terminal could not complete the request. Try again later.',
  session_expired: 'This session has ended. Open the terminal again.',
  origin_rejected: 'This page is not allowed to open the terminal.',
  access_denied: 'This action is not allowed for this session.',
  customer_not_found: 'The customer was not found.',
  customer_ambiguous: 'More than one customer matches. Contact support.',
  duplicate_card: 'This card is already saved for this customer.',
  invalid_request: 'Some card details are not valid. Check them and try again.',
  processor_decline: 'The card was declined. Try another card.',
  attempt_limit: 'This session cannot take another payment.',
  payment_unknown:
    'We could not confirm the payment. Do not pay again. Contact the merchant to check it.',
  rate_limited: 'Too many attempts. Wait a moment and try again.',
  version_unsupported: 'This terminal version is not supported.',
  unavailable: 'The terminal is not available. Try again later.'
}

const STATUS_CODES: Record<number, ErrorCode> = {
  400: 'invalid_request',
  401: 'session_expired',
  402: 'processor_decline',
  403: 'access_denied',
  410: 'session_expired',
  422: 'invalid_request',
  429: 'rate_limited',
  500: 'server_error'
}

/** An API failure carrying a protocol error code and user-facing copy. */
export class TerminalApiError extends Error {
  readonly code: ErrorCode

  constructor(code: ErrorCode) {
    super(MESSAGES[code])
    this.name = 'TerminalApiError'
    this.code = code
  }
}

export interface TerminalApiConfig {
  baseUrl: string
  timeoutMs?: number
  paymentTimeoutMs?: number
  fetch?: typeof fetch
}

export interface ExchangeInput {
  clientSecret: string
  parentOrigin: string
  terminalId: string
}

export interface TerminalApi {
  exchange: (
    input: ExchangeInput
  ) => Promise<{ idToken: string; session: SessionConfig }>
  listMethods: (idToken: string) => Promise<PaymentMethodSummary[]>
  createCardMethod: (
    idToken: string,
    draft: CardDraft,
    idempotencyKey: string
  ) => Promise<PaymentMethodSummary>
  createPaymentAttempt: (
    idToken: string,
    paymentMethodId: number
  ) => Promise<PaymentQuote>
  submitPayment: (
    idToken: string,
    attemptId: number,
    idempotencyKey: string
  ) => Promise<SubmitOutcome>
  readPaymentAttempt: (
    idToken: string,
    attemptId: number
  ) => Promise<AttemptState>
}

/**
 * Returns the user-facing copy for an error code.
 * @param code - The protocol error code.
 * @returns The message.
 */
export function messageFor(code: ErrorCode): string {
  return MESSAGES[code]
}

function toMethod(value: unknown): PaymentMethodSummary | null {
  if (!isRecord(value)) return null
  const { paymentType, brand, last4 } = value
  const paymentMethodId = paymentMethodPkid(value.paymentMethodId)
  const expMonth = integerOrNull(value.expMonth)
  const expYear = integerOrNull(value.expYear)
  if (paymentMethodId === null) return null
  if (paymentType !== 'card' && paymentType !== 'ach') return null
  if (brand !== null && typeof brand !== 'string') return null
  if (typeof last4 !== 'string' || !/^\d{4}$/.test(last4)) return null
  if (expMonth === undefined || expYear === undefined) return null
  return { paymentMethodId, paymentType, brand, last4, expMonth, expYear }
}

function toSession(body: Record<string, unknown>): SessionConfig | null {
  const { actions, actionId, sessionExpiresAt, locale } = body
  if (!isStringArray(actions)) {
    return null
  }
  if (typeof actionId !== 'string' || typeof sessionExpiresAt !== 'string') {
    return null
  }
  const theme = validateTheme(body.theme ?? {})
  return {
    actions,
    actionId,
    sessionExpiresAt,
    theme: theme.ok ? theme.theme : {},
    locale: typeof locale === 'string' ? locale : 'en-US'
  }
}

function minorUnits(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : null
}

function toCard(value: unknown): CardSummary | null {
  if (!isRecord(value)) return null
  const { brand, last4 } = value
  if (brand !== null && typeof brand !== 'string') return null
  if (typeof last4 !== 'string' || !/^\d{4}$/.test(last4)) return null
  return { brand, last4 }
}

function toAmounts(
  value: Record<string, unknown>
): Pick<PaymentQuote, 'amount' | 'fee' | 'total' | 'currency'> | null {
  const amount = minorUnits(value.amount)
  const fee = minorUnits(value.fee)
  const total = minorUnits(value.total)
  const { currency } = value
  if (amount === null || amount === 0 || fee === null || total === null) {
    return null
  }
  if (total !== amount + fee) return null
  if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return null
  return { amount, fee, total, currency }
}

function toQuote(value: Record<string, unknown>): PaymentQuote | null {
  const attemptId = paymentMethodPkid(value.attemptId)
  const amounts = toAmounts(value)
  const method = toCard(value.method)
  if (attemptId === null || amounts === null || method === null) return null
  return { attemptId, ...amounts, method }
}

function toPayment(value: unknown): PaymentRecord | null {
  if (!isRecord(value)) return null
  const paymentId = paymentMethodPkid(value.paymentId)
  const amounts = toAmounts(value)
  const method = toCard(value.method)
  const { status } = value
  if (paymentId === null || amounts === null || method === null) return null
  if (status !== 'paid' && status !== 'pending') return null
  return { paymentId, status, ...amounts, method }
}

function toDecline(value: unknown): PaymentDecline {
  const body = isRecord(value) ? value : {}
  const { declineReason, attemptsRemaining } = body
  const reason =
    typeof declineReason === 'string' &&
    declineReason.trim() !== '' &&
    declineReason.length <= MAX_DECLINE_REASON
      ? declineReason.trim()
      : MESSAGES.processor_decline
  const remaining =
    typeof attemptsRemaining === 'number' &&
    Number.isInteger(attemptsRemaining) &&
    attemptsRemaining >= 0
      ? Math.min(attemptsRemaining, MAX_PAYMENT_ATTEMPTS)
      : 0
  return { reason, attemptsRemaining: remaining }
}

function toAttempt(body: Record<string, unknown>): AttemptState | null {
  switch (body.status) {
    case 'pending':
    case 'submitted':
    case 'unknown':
      return { status: body.status }
    case 'succeeded': {
      const payment = toPayment(body.payment)
      return payment ? { status: 'succeeded', payment } : null
    }
    case 'declined':
      return { status: 'declined', decline: toDecline(body) }
    default:
      return null
  }
}

function errorFor(
  status: number,
  body: unknown,
  codes: Record<number, ErrorCode> = STATUS_CODES
): TerminalApiError {
  const code = isRecord(body) ? body.code : undefined
  if (isErrorCode(code)) return new TerminalApiError(code)
  return new TerminalApiError(codes[status] ?? 'unavailable')
}

const ATTEMPT_CODES: Record<number, ErrorCode> = {
  ...STATUS_CODES,
  409: 'attempt_limit'
}

// The server answered these before it charged the card.
const UNCHARGED: ReadonlySet<number> = new Set([
  400, 401, 403, 404, 410, 422, 429
])

/**
 * Creates the client for the terminal session and payment-method routes.
 * @param config - The API origin and an optional fetch implementation.
 * @returns The client.
 */
export function createTerminalApi(config: TerminalApiConfig): TerminalApi {
  const fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis)
  const timeoutMs = config.timeoutMs ?? 30_000
  const paymentTimeoutMs = config.paymentTimeoutMs ?? PAYMENT_TIMEOUT_MS

  interface CallInit {
    method: string
    headers?: Record<string, string>
    body?: unknown
    timeoutMs?: number
    codes?: Record<number, ErrorCode>
  }

  function send(path: string, init: CallInit): Promise<Response> {
    return fetchImpl(`${config.baseUrl}${path}`, {
      method: init.method,
      headers: {
        accept: 'application/json',
        ...(init.body === undefined
          ? {}
          : { 'content-type': 'application/json' }),
        ...init.headers
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: AbortSignal.timeout(init.timeoutMs ?? timeoutMs)
    })
  }

  async function call(
    path: string,
    init: CallInit
  ): Promise<Record<string, unknown>> {
    let response: Response
    try {
      response = await send(path, init)
    } catch {
      throw new TerminalApiError('unavailable')
    }
    const body: unknown = await response.json().catch(() => null)
    if (!response.ok) throw errorFor(response.status, body, init.codes)
    if (!isRecord(body)) throw new TerminalApiError('unavailable')
    return body
  }

  return {
    async exchange(input) {
      const body = await call(TERMINAL_PATHS.exchange, {
        method: 'POST',
        body: input
      })
      const session = toSession(body)
      if (typeof body.idToken !== 'string' || session === null) {
        throw new TerminalApiError('unavailable')
      }
      return { idToken: body.idToken, session }
    },
    async listMethods(idToken) {
      const body = await call(TERMINAL_PATHS.paymentMethods, {
        method: 'GET',
        headers: { authorization: `Bearer ${idToken}` }
      })
      const list = Array.isArray(body.paymentMethods) ? body.paymentMethods : []
      return list
        .map(toMethod)
        .filter((m): m is PaymentMethodSummary => m !== null)
    },
    async createCardMethod(idToken, draft, idempotencyKey) {
      const body = await call(TERMINAL_PATHS.paymentMethods, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
          'idempotency-key': idempotencyKey
        },
        body: draft
      })
      const method = toMethod(body)
      if (method === null) throw new TerminalApiError('unavailable')
      return method
    },
    async createPaymentAttempt(idToken, paymentMethodId) {
      const body = await call(TERMINAL_PATHS.paymentAttempts, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}` },
        body: { paymentMethodId },
        codes: ATTEMPT_CODES
      })
      const quote = toQuote(body)
      if (quote === null) throw new TerminalApiError('unavailable')
      return quote
    },
    async submitPayment(idToken, attemptId, idempotencyKey) {
      let response: Response
      try {
        response = await send(TERMINAL_PATHS.payments, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${idToken}`,
            'idempotency-key': idempotencyKey
          },
          body: { attemptId },
          timeoutMs: paymentTimeoutMs
        })
      } catch {
        return { kind: 'unknown' }
      }
      const body: unknown = await response.json().catch(() => null)
      if (response.ok) {
        const payment = toPayment(body)
        return payment ? { kind: 'succeeded', payment } : { kind: 'unknown' }
      }
      if (response.status === 402) {
        return { kind: 'declined', decline: toDecline(body) }
      }
      const limited =
        response.status === 409 &&
        isRecord(body) &&
        body.code === 'attempt_limit'
      if (UNCHARGED.has(response.status) || limited) {
        throw errorFor(response.status, body, ATTEMPT_CODES)
      }
      return { kind: 'unknown' }
    },
    async readPaymentAttempt(idToken, attemptId) {
      const body = await call(
        `${TERMINAL_PATHS.paymentAttempts}/${attemptId}`,
        { method: 'GET', headers: { authorization: `Bearer ${idToken}` } }
      )
      const attempt = toAttempt(body)
      if (attempt === null) throw new TerminalApiError('unavailable')
      return attempt
    }
  }
}
